import { useEffect, useState } from 'react';
import { api, type Asset } from '../../lib/api';
import { KindTabs, Thumb, displayName, sizeOf, sortAssets, type Kind, type SortKey } from '../AssetGrid';
import { OpenInPro, Stage, useAssetUrl } from './parts';
import type { TabProps } from './Quick';

// The Library tab: the same library the Pro bench shows, with fewer knobs.
//
// Everything the other five tabs make lands here, because everything they
// make is an ordinary asset — nothing a Quick tab makes is a special case.
// Pick one and it opens large on the stage with the four things a new
// creator does next: edit it, match it, put it in a shot, put it in the
// movie. Rename and delete are here too, because a library nobody can tidy
// is a library nobody trusts. Groups, notes, describing and the template
// store stay on the Pro bench; "Open in Pro" is where they are.

export function QuickLibrary({ brand, onPass, onOpenInPro, onError }: TabProps) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [kind, setKind] = useState<Kind | 'all'>('all');
  const [sort, setSort] = useState<SortKey>('newest');
  const [picked, setPicked] = useState<Asset | null>(null);
  const [busy, setBusy] = useState(false);
  const brandId = brand?.id ?? null;

  async function load() {
    try { const all = await api.assets(brandId); setAssets(all); setPicked((p) => (p ? all.find((a) => a.id === p.id) ?? null : null)); }
    catch (e) { onError((e as Error).message); }
  }
  useEffect(() => { void load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [brandId]);

  const shown = sortAssets(kind === 'all' ? assets : assets.filter((a) => a.kind === kind), sort);
  const url = useAssetUrl(picked);

  async function rename(a: Asset) {
    const name = window.prompt('Call it', displayName(a));
    if (name === null) return;
    setBusy(true);
    try { await api.updateAsset(a.id, { name: name.trim() || null }); await load(); }
    catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  }
  async function remove(a: Asset) {
    // Names the thing: a library of similar files is exactly where "this asset" gets clicked through.
    if (!window.confirm(`Delete ${displayName(a)}? The file goes too, and that cannot be undone.`)) return;
    setBusy(true);
    try { await api.deleteAsset(a.id); setPicked(null); await load(); }
    catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  }

  const isPicture = picked?.kind === 'image' && picked.status === 'ready';
  const isFootage = (picked?.kind === 'image' || picked?.kind === 'video') && picked.status === 'ready';

  return (
    <div className="quick-tab">
      <Stage empty={assets.length ? 'Pick something below to see it large.' : 'Nothing here yet. Make a picture, a bed or a shot and it lands here.'}>
        {picked && url ? (
          picked.kind === 'video' ? <video src={url} controls autoPlay />
            : picked.kind === 'audio' ? <div className="quick-stage-audio"><span>{displayName(picked)}</span><audio src={url} controls autoPlay /></div>
            : picked.kind === 'image' ? <img src={url} alt={displayName(picked)} />
            : <p className="quick-stage-empty">{displayName(picked)} · a document has nothing to show. Open in Pro to download it.</p>
        ) : picked ? <p className="quick-stage-empty">{picked.status === 'ready' ? 'Opening…' : picked.status === 'error' ? (picked.error ?? 'The render plane could not use it.') : 'Still developing — the render plane is looking at it.'}</p> : null}
      </Stage>

      {picked && (
        <div className="quick-actions" data-tool="quick-picked">
          <strong className="quick-picked-name" title={displayName(picked)}>{displayName(picked)}</strong>
          <span className="sub-dim">{picked.kind} · {sizeOf(picked.bytes)}</span>
          {isPicture && <button type="button" className="btn tiny" disabled={busy} onClick={() => onPass({ tab: 'edit', assetId: picked.id })}>Edit this</button>}
          {isPicture && <button type="button" className="btn tiny" disabled={busy} title="A new picture that keeps to this one" onClick={() => onPass({ tab: 'image', assetId: picked.id })}>Match in Image</button>}
          {isPicture && <button type="button" className="btn tiny" disabled={busy} title="A shot described from this picture" onClick={() => onPass({ tab: 'video', assetId: picked.id })}>Use in Video</button>}
          {isFootage && <button type="button" className="btn tiny" disabled={busy} onClick={() => onPass({ tab: 'movie', assetId: picked.id })}>Use in Movie</button>}
          <button type="button" className="btn tiny" disabled={busy} onClick={() => void rename(picked)}>Rename</button>
          <button type="button" className="btn tiny" disabled={busy} onClick={() => void remove(picked)}>Delete</button>
        </div>
      )}

      <div className="row wrap" style={{ gap: 10, alignItems: 'center' }}>
        <KindTabs assets={assets} value={kind} onChange={setKind} />
        <span style={{ flex: 1 }} />
        <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} title="Order">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="name">Name</option>
          <option value="largest">Largest first</option>
          <option value="kind">Kind</option>
        </select>
        <OpenInPro what="The Assets bench: groups, notes, describing, uploads and the template store." onClick={() => onOpenInPro({ bench: 'assets', move: null })} />
      </div>

      {shown.length === 0 ? (
        <div className="empty">{assets.length === 0 ? 'Nothing here yet.' : `No ${kind === 'all' ? '' : kind + ' '}assets match.`}</div>
      ) : (
        <div className="quick-grid" data-tool="quick-library-grid">
          {shown.map((a) => (
            <button key={a.id} type="button" className={`quick-grid-item${picked?.id === a.id ? ' on' : ''}${a.status !== 'ready' ? ' unready' : ''}`} onClick={() => setPicked(a)} title={displayName(a)}>
              <Thumb asset={a} size={150} />
              <span className="quick-grid-name">{displayName(a)}</span>
              <span className="sub-dim">{a.kind}{a.status !== 'ready' ? ` · ${a.status === 'error' ? 'failed' : 'developing…'}` : ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
